import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Fingerprint, Trash2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from './ui/alert-dialog';
import {
  biometricsAvailable,
  enrollBiometric,
  listBiometricCredentials,
  removeBiometricCredential,
  disableBiometricLogin,
} from '../lib/biometric';

/**
 * BiometricSettings — profile-page card that exposes the "Enable Biometric
 * Login" toggle + the list of currently-enrolled authenticators. Uses the
 * WebAuthn API on the web; the mobile apps hit the same backend routes
 * through the Expo `expo-local-authentication` bridge.
 */
export default function BiometricSettings() {
  const [supported, setSupported] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDisable, setConfirmDisable] = useState(false);

  const refresh = async () => {
    const data = await listBiometricCredentials();
    setCreds(data.credentials || []);
    setEnabled((data.credentials || []).length > 0);
    setError('');
  };

  useEffect(() => {
    setSupported(biometricsAvailable());
    const reconcile = () => refresh().catch(e => setError(e.message)).finally(() => setLoading(false));
    reconcile();
    window.addEventListener('focus', reconcile);
    return () => window.removeEventListener('focus', reconcile);
  }, []);

  const onToggle = async (nextEnabled) => {
    if (busy) return;
    if (nextEnabled) {
      // Enroll a new authenticator on this device.
      setBusy(true);
      try {
        const nickname = `${window.navigator.platform || 'This device'} · ${new Date().toLocaleDateString()}`;
        await enrollBiometric(nickname);
        toast.success('Biometric login enabled on this device.');
        await refresh();
      } catch (e) {
        toast.error(e.message || 'Enrollment failed.');
        await refresh().catch(err => setError(err.message));
      } finally {
        setBusy(false);
      }
    } else {
      // Remove every credential — user opted OUT globally.
      if (!creds.length) { setEnabled(false); return; }
      setConfirmDisable(true);
    }
  };

  const disableAll = async () => {
      setConfirmDisable(false);
      setBusy(true);
      try {
        await disableBiometricLogin();
        toast.success('Biometric login disabled.');
        await refresh();
      } catch (e) {
        toast.error(e.message);
        await refresh().catch(err => setError(err.message));
      } finally {
        setBusy(false);
      }
  };

  const removeOne = async (credId) => {
    setBusy(true);
    try {
      await removeBiometricCredential(credId);
      toast.success('Authenticator removed.');
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="p-4 sm:p-5 border-2 bg-white"
      style={{ borderColor: 'rgba(201, 169, 98, 0.4)' }}
      data-testid="biometric-settings-card"
    >
      <AlertDialog open={confirmDisable} onOpenChange={setConfirmDisable}>
        <AlertDialogContent data-testid="biometric-disable-dialog">
          <AlertDialogHeader><AlertDialogTitle>Disable biometric login?</AlertDialogTitle><AlertDialogDescription>Remove the account’s enrolled passkeys. Your password and current session will remain available.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="biometric-disable-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={disableAll} data-testid="biometric-disable-confirm">Disable</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(201,169,98,0.15)', border: '1px solid rgba(201,169,98,0.5)' }}
          >
            <Fingerprint className="h-5 w-5" style={{ color: '#C9A962' }} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold" style={{ color: '#000' }}>Biometric login</p>
            <p className="text-xs text-zinc-500">
              Fingerprint, Face ID or device passkey. Password login stays available.
            </p>
          </div>
        </div>
        <Switch
          checked={enabled}
          className="data-[state=checked]:bg-[#C9A962] data-[state=unchecked]:bg-zinc-300 [&>span]:bg-white"
          onCheckedChange={onToggle}
          disabled={(!supported && !enabled) || busy || loading || !!error}
          aria-label="Biometric login"
          data-testid="biometric-toggle"
        />
      </div>
      <p className="mt-2 text-xs text-zinc-600" role="status" data-testid="biometric-status">{loading ? 'Checking…' : error ? 'Status unavailable' : enabled ? 'Enabled' : 'Disabled'}</p>
      {error && <div className="mt-2 text-xs text-red-700" role="alert" data-testid="biometric-status-error">{error}<button type="button" className="ml-2 underline" onClick={() => { setLoading(true); refresh().catch(e => setError(e.message)).finally(() => setLoading(false)); }} data-testid="biometric-status-retry">Retry</button></div>}

      {!supported && (
        <p className="mt-3 text-[11px] text-red-600">
          This browser does not expose the WebAuthn API. Try Chrome, Safari or Edge on a device with Fingerprint / FaceID.
        </p>
      )}

      {creds.length > 0 && (
        <div className="mt-4 pt-3 border-t border-zinc-200 space-y-1">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Enrolled devices</p>
          {creds.map((c) => (
            <div
              key={c.credential_id}
              className="flex items-center justify-between gap-2 p-2 rounded-md bg-zinc-50 border border-zinc-200"
              data-testid={`biometric-cred-${c.credential_id.slice(0, 8)}`}
            >
              <div className="min-w-0 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 flex-shrink-0" style={{ color: '#16a34a' }} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{c.nickname || 'Unnamed device'}</p>
                  <p className="text-[10px] text-zinc-500 truncate">
                    Added {new Date(c.created_at).toLocaleDateString()}
                    {c.last_used_at ? ` · Last used ${new Date(c.last_used_at).toLocaleDateString()}` : ' · Never used'}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeOne(c.credential_id)}
                className="text-red-600 hover:bg-red-50"
                disabled={busy}
                data-testid={`biometric-remove-${c.credential_id.slice(0, 8)}`}
                title="Remove authenticator" aria-label={`Remove ${c.nickname || 'authenticator'}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
